import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { dashIfEmpty, formLabel, packSizeLabel } from '../utils/formatters';

// Label/value pair. A fixed label column keeps the values aligned so the panel
// scans down a column rather than being read sentence by sentence.
const DetailRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', py: 0.35 }}>
    <Typography
      variant='caption'
      sx={{ color: 'text.secondary', width: 104, flexShrink: 0, letterSpacing: '.02em' }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * What the preference being written is about, spelled out.
 *
 * In Change mode this is the only statement of the drug; in the Add modes it fills
 * in as the pickers are used, so the same panel answers "what am I about to lock?"
 * either way. Current NDC appears here as well as beside its row in the list: the
 * row says which option it is, this says what the partner has today.
 */
const PreferenceContextPanel = ({ partner, drug, currentNdc, currentManufacturer }) => {
  if (!partner && !drug) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover',
        px: 1.5,
        py: 1,
        mb: 2,
      }}
    >
      {partner && (
        <DetailRow label='Partner'>
          <Typography variant='body2' sx={{ fontWeight: 700 }}>
            {partner.partnerName}
          </Typography>
        </DetailRow>
      )}

      {drug && (
        <>
          <DetailRow label='Drug (Catalog)'>
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              {dashIfEmpty(drug.drugName)}
            </Typography>
          </DetailRow>

          <DetailRow label='Strength'>
            <Typography variant='body2'>{dashIfEmpty(drug.strength)}</Typography>
          </DetailRow>

          <DetailRow label='Product ID'>
            <Box component='span' sx={{ fontFamily: 'monospace', fontSize: 13 }}>
              {drug.productId}
            </Box>
          </DetailRow>

          <DetailRow label='Form'>
            <Typography variant='body2'>{dashIfEmpty(formLabel(drug))}</Typography>
          </DetailRow>

          <DetailRow label='Pack Size'>
            <Typography variant='body2'>{dashIfEmpty(packSizeLabel(drug))}</Typography>
          </DetailRow>
        </>
      )}

      {currentNdc && (
        <DetailRow label='Current NDC'>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Box component='span' sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>
              {currentNdc}
            </Box>
            {currentManufacturer && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {currentManufacturer}
              </Typography>
            )}
          </Box>
        </DetailRow>
      )}
    </Box>
  );
};

PreferenceContextPanel.propTypes = {
  partner: PropTypes.shape({ partnerName: PropTypes.string }),
  drug: PropTypes.shape({
    productId: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    dosageForm: PropTypes.string,
    formAlias: PropTypes.string,
    packSizeValue: PropTypes.string,
    packSizeUnit: PropTypes.string,
  }),
  currentNdc: PropTypes.string,
  currentManufacturer: PropTypes.string,
};

PreferenceContextPanel.defaultProps = {
  partner: null,
  drug: null,
  currentNdc: '',
  currentManufacturer: '',
};

export default PreferenceContextPanel;
